import axiosInstance from "../api.axiosInstance"
import type {
  BatteryArbitrageDto,
  BatterySavingsVisibility,
  UpdateBatteryArbitrageSettingsDto,
  UpdateBatterySavingsInputDto,
} from "../dto/battery-arbitrage.dto"
import type {
  BatteryConcertApplicationStatusDto,
  BatteryConcertCustomerContextDto,
  InitiateBatteryConcertApplicationDto,
  SubmitBatteryConcertApplicationDto,
  UploadBatteryConcertDocumentDto,
} from "../dto/battery-concert-application.dto"
import type { BatteryContractReadinessDto } from "../dto/battery-contract.dto"
import type {
  BatteryComputePreviewDto,
  BatteryProposalComputeDto,
  UpdateBatteryProposalComputeDto,
} from "../dto/battery-proposal-compute.dto"
import type {
  BatteryProposalDto,
  BatteryProposalExtendedWarrantiesDto,
  BatteryProposalFinancialSummaryResponseDto,
  BatteryProposalGenabilityAccountDto,
  BatteryProposalUtilityCompanyDto,
  BatteryProposalTariffCandidateDto,
  BatteryProposalIncentivesDto,
  BatteryProposalInputsDto,
  BatteryProposalPricingDto,
  PreviewBatteryProposalEquipmentDto,
  RefreshBatteryProposalAddersResultDto,
  UpdateBatteryProposalAddersDto,
  UpdateBatteryProposalDetailsDto,
  UpdateBatteryProposalExtendedWarrantyDto,
  UpdateBatteryProposalIncentivesDto,
  BatteryProposalVppProgramsDto,
  UpdateBatteryProposalVppProgramsDto,
  UpdateBatteryProposalTariffDto,
  UpdateBatteryExistingBatteryDto,
  BatteryConcertOffersDto,
  BatteryLightReachOffersDto,
  BatteryProposalFinancingDto,
  BatterySungageQuotePreviewDto,
  CalculateBatteryPaymentDto,
  PreviewBatterySungageQuoteDto,
  SaveBatteryConcertOfferDto,
  SaveBatteryLightReachOfferDto,
  SaveBatterySungageQuoteDto,
  SubmitBatteryLightReachApplicationDto,
  ApplyBatteryCreditHumanApplicationDto,
  BatteryCreditHumanApplicationHandoffDto,
  BatterySungageApplicationHandoffDto,
  BatterySungageApplicationReadDto,
  CreateBatterySungageApplicationDto,
  ReplaceBatteryCreditHumanApplicationDto,
} from "../dto/battery-proposal.dto"
import type { BatterySupplementalStatusDto } from "../dto/battery-supplemental.dto"
import type { LightReachDisclosureDto } from "../dto/light-reach.dto"
import type { ProposalDebugRequestBodyDto } from "../dto/proposal.dto"

/** PowurOS-backed writes, as the Solar service allows them. */
const PROVIDER_WRITE_TIMEOUT_MS = 120_000

const debugRequestErrorMessage = (error: unknown): string | undefined => {
  const { response } = error as {
    response?: { data?: { message?: unknown } }
  }
  const message = response?.data?.message

  if (typeof message === `string` && message.trim() !== ``) return message
  if (
    Array.isArray(message) &&
    message.length > 0 &&
    message.every((entry) => typeof entry === `string`)
  ) {
    return message.join(`, `)
  }
  return undefined
}

const fetchDebugRequest = async (
  proposalId: string,
  path: `inputs` | `pricing`,
  signal?: AbortSignal,
): Promise<ProposalDebugRequestBodyDto> => {
  try {
    const url = `/battery-proposal/v2/${proposalId}/debug/${path}`
    const response = signal
      ? await axiosInstance.get<ProposalDebugRequestBodyDto>(url, { signal })
      : await axiosInstance.get<ProposalDebugRequestBodyDto>(url)
    return response.data
  } catch (error: unknown) {
    const serverMessage = debugRequestErrorMessage(error)
    if (serverMessage !== undefined) {
      throw new Error(serverMessage)
    }
    throw error
  }
}

export class BatteryProposalService {
  async getById(proposalId: string): Promise<BatteryProposalDto> {
    const response = await axiosInstance.get<BatteryProposalDto>(
      `/battery-proposal/v2/${proposalId}`,
    )

    return response.data
  }

  async getPricing(proposalId: string): Promise<BatteryProposalPricingDto> {
    const response = await axiosInstance.get<BatteryProposalPricingDto>(
      `/battery-proposal/v2/${proposalId}/pricing`,
    )

    return response.data
  }

  async postPricing(proposalId: string): Promise<BatteryProposalPricingDto> {
    // Calculate / Refresh is a bodyless command; the gateway builds the
    // provider request from persisted state only.
    const response = await axiosInstance.post<BatteryProposalPricingDto>(
      `/battery-proposal/v2/${proposalId}/pricing`,
    )

    return response.data
  }

  async completeInitialSetup(proposalId: string): Promise<BatteryProposalDto> {
    // Bodyless: the gateway decides completion from persisted setup and the
    // stored price, and answers with the same render contract as the GET.
    const response = await axiosInstance.post<BatteryProposalDto>(
      `/battery-proposal/v2/${proposalId}/initial-setup`,
    )

    return response.data
  }

  async getContractReadiness(
    proposalId: string,
  ): Promise<BatteryContractReadinessDto> {
    const response = await axiosInstance.get<BatteryContractReadinessDto>(
      `/battery-proposal/v2/${proposalId}/contract`,
    )
    return response.data
  }

  async sendContract(
    proposalId: string,
    body: { idempotencyKey: string },
  ): Promise<BatteryContractReadinessDto> {
    const response = await axiosInstance.post<BatteryContractReadinessDto>(
      `/battery-proposal/v2/${proposalId}/contract`,
      body,
    )
    return response.data
  }

  async getFinancialSummary(
    proposalId: string,
  ): Promise<BatteryProposalFinancialSummaryResponseDto> {
    const response =
      await axiosInstance.get<BatteryProposalFinancialSummaryResponseDto>(
        `/battery-proposal/v2/${proposalId}/financial-summary`,
      )

    return response.data
  }

  async getFinancing(proposalId: string): Promise<BatteryProposalFinancingDto> {
    const response = await axiosInstance.get<BatteryProposalFinancingDto>(
      `/battery-proposal/v2/${proposalId}/financing`,
    )

    return response.data
  }

  async getConcertOffers(proposalId: string): Promise<BatteryConcertOffersDto> {
    const response = await axiosInstance.get<BatteryConcertOffersDto>(
      `/battery-proposal/v2/${proposalId}/financing/concert-offers`,
    )

    return response.data
  }

  /**
   * Save a returned Concert offer. The body carries identity only: the server
   * re-fetches offers for its own current price and owns the payment.
   */
  async saveConcertOffer(
    proposalId: string,
    body: SaveBatteryConcertOfferDto,
  ): Promise<BatteryProposalFinancingDto> {
    const response = await axiosInstance.put<BatteryProposalFinancingDto>(
      `/battery-proposal/v2/${proposalId}/financing/concert-offer`,
      body,
    )

    return response.data
  }

  /** LightReach estimated pricing for the current price. Creates nothing at LightReach. */
  async getLightReachOffers(
    proposalId: string,
  ): Promise<BatteryLightReachOffersDto> {
    const response = await axiosInstance.get<BatteryLightReachOffersDto>(
      `/battery-proposal/v2/${proposalId}/financing/lightreach-offers`,
    )

    return response.data
  }

  /**
   * Save a returned LightReach offer. The server creates the LightReach
   * account and quote for its own current price and owns the payment.
   */
  async saveLightReachOffer(
    proposalId: string,
    body: SaveBatteryLightReachOfferDto,
  ): Promise<BatteryProposalFinancingDto> {
    const response = await axiosInstance.put<BatteryProposalFinancingDto>(
      `/battery-proposal/v2/${proposalId}/financing/lightreach-offer`,
      body,
    )

    return response.data
  }

  /** LightReach disclosures the applicant must read and accept. */
  async getLightReachApplicationDisclosures(
    proposalId: string,
  ): Promise<LightReachDisclosureDto[]> {
    const response = await axiosInstance.get<LightReachDisclosureDto[]>(
      `/battery-proposal/v2/${proposalId}/lightreach-application/disclosures`,
    )

    return response.data
  }

  /**
   * Submit the LightReach credit application against the saved, current
   * offer. Returns the financing read after the submission.
   */
  async submitLightReachApplication(
    proposalId: string,
    body: SubmitBatteryLightReachApplicationDto,
  ): Promise<BatteryProposalFinancingDto> {
    const response = await axiosInstance.post<BatteryProposalFinancingDto>(
      `/battery-proposal/v2/${proposalId}/lightreach-application`,
      body,
    )

    return response.data
  }

  /**
   * Ask LightReach to send its contract for the approved application and
   * saved offer. Open stipulations do not hold the send.
   */
  async sendLightReachContract(
    proposalId: string,
  ): Promise<BatteryProposalFinancingDto> {
    const response = await axiosInstance.post<BatteryProposalFinancingDto>(
      `/battery-proposal/v2/${proposalId}/lightreach-contract`,
    )

    return response.data
  }

  /** Re-read the contract from LightReach and resume a missed handoff. */
  async refreshLightReachContract(
    proposalId: string,
  ): Promise<BatteryProposalFinancingDto> {
    const response = await axiosInstance.post<BatteryProposalFinancingDto>(
      `/battery-proposal/v2/${proposalId}/lightreach-contract/refresh`,
    )

    return response.data
  }

  /** Send the Powur supplemental documents again after a failed send. */
  async retryLightReachSupplemental(
    proposalId: string,
    attemptId: string,
  ): Promise<BatteryProposalFinancingDto> {
    const response = await axiosInstance.post<BatteryProposalFinancingDto>(
      `/battery-proposal/v2/${proposalId}/lightreach-contract/supplemental/${attemptId}/retry`,
    )

    return response.data
  }

  /**
   * Preview a BrightStart payment. The body is identity only: the server
   * resolves price and terms and does not change the saved Proposal.
   */
  async previewSungageQuote(
    proposalId: string,
    body: PreviewBatterySungageQuoteDto,
  ): Promise<BatterySungageQuotePreviewDto> {
    const response = await axiosInstance.post<BatterySungageQuotePreviewDto>(
      `/battery-proposal/v2/${proposalId}/financing/sungage-quote/preview`,
      body,
    )

    return response.data
  }

  /**
   * Save a BrightStart Loan Product and its provider Quote. Like Concert save,
   * a 409 means the preview's pricing context is obsolete.
   */
  async saveSungageQuote(
    proposalId: string,
    body: SaveBatterySungageQuoteDto,
  ): Promise<BatteryProposalFinancingDto> {
    const response = await axiosInstance.put<BatteryProposalFinancingDto>(
      `/battery-proposal/v2/${proposalId}/financing/sungage-quote`,
      body,
    )

    return response.data
  }

  async calculatePayment(
    proposalId: string,
    body: CalculateBatteryPaymentDto,
  ): Promise<BatteryProposalFinancingDto> {
    const response = await axiosInstance.post<BatteryProposalFinancingDto>(
      `/battery-proposal/v2/${proposalId}/financing/calculate-payment`,
      body,
    )

    return response.data
  }

  async applyCreditHumanApplication(
    proposalId: string,
    body: ApplyBatteryCreditHumanApplicationDto,
  ): Promise<BatteryCreditHumanApplicationHandoffDto> {
    const response =
      await axiosInstance.post<BatteryCreditHumanApplicationHandoffDto>(
        `/battery-proposal/v2/${proposalId}/financing/credit-human/apply`,
        body,
      )

    return response.data
  }

  async reopenCreditHumanApplication(
    proposalId: string,
    applicationId: string,
  ): Promise<BatteryCreditHumanApplicationHandoffDto> {
    const response =
      await axiosInstance.post<BatteryCreditHumanApplicationHandoffDto>(
        `/battery-proposal/v2/${proposalId}/financing/credit-human/applications/${applicationId}/reopen`,
      )

    return response.data
  }

  async replaceCreditHumanApplication(
    proposalId: string,
    applicationId: string,
    body: ReplaceBatteryCreditHumanApplicationDto,
  ): Promise<BatteryCreditHumanApplicationHandoffDto> {
    const response =
      await axiosInstance.post<BatteryCreditHumanApplicationHandoffDto>(
        `/battery-proposal/v2/${proposalId}/financing/credit-human/applications/${applicationId}/replace`,
        body,
      )

    return response.data
  }

  async markApprovedCreditHumanApplication(
    proposalId: string,
    applicationId: string,
  ): Promise<BatteryProposalFinancingDto> {
    const response = await axiosInstance.post<BatteryProposalFinancingDto>(
      `/battery-proposal/v2/${proposalId}/financing/credit-human/applications/${applicationId}/mark-approved`,
    )

    return response.data
  }

  async startSungageApplication(
    proposalId: string,
    body: CreateBatterySungageApplicationDto,
  ): Promise<BatterySungageApplicationHandoffDto> {
    const response =
      await axiosInstance.post<BatterySungageApplicationHandoffDto>(
        `/battery-proposal/v2/${proposalId}/financing/sungage-applications`,
        body,
      )
    return response.data
  }

  async resumeSungageApplication(
    proposalId: string,
    applicationId: string,
  ): Promise<BatterySungageApplicationHandoffDto> {
    const response =
      await axiosInstance.post<BatterySungageApplicationHandoffDto>(
        `/battery-proposal/v2/${proposalId}/financing/sungage-applications/${applicationId}/resume`,
      )
    return response.data
  }

  async getSungageApplication(
    proposalId: string,
    applicationId: string,
  ): Promise<BatterySungageApplicationReadDto> {
    const response = await axiosInstance.get<BatterySungageApplicationReadDto>(
      `/battery-proposal/v2/${proposalId}/financing/sungage-applications/${applicationId}`,
    )
    return response.data
  }

  async refreshSungageApplication(
    proposalId: string,
    applicationId: string,
  ): Promise<BatterySungageApplicationReadDto> {
    const response = await axiosInstance.post<BatterySungageApplicationReadDto>(
      `/battery-proposal/v2/${proposalId}/financing/sungage-applications/${applicationId}/refresh`,
    )
    return response.data
  }

  async getConcertApplication(
    proposalId: string,
  ): Promise<BatteryConcertApplicationStatusDto> {
    const response = await axiosInstance.get<BatteryConcertApplicationStatusDto>(
      `/battery-proposal/v2/${proposalId}/concert-application`,
    )

    return response.data
  }

  /** Reserve Now: create or reuse this Proposal's Concert customer. Never submits credit. */
  async initiateConcertApplication(
    proposalId: string,
    body: InitiateBatteryConcertApplicationDto,
  ): Promise<BatteryConcertCustomerContextDto> {
    const response = await axiosInstance.post<BatteryConcertCustomerContextDto>(
      `/battery-proposal/v2/${proposalId}/concert-application/initiate`,
      body,
    )

    return response.data
  }

  async submitConcertApplication(
    proposalId: string,
    body: SubmitBatteryConcertApplicationDto,
  ): Promise<BatteryConcertApplicationStatusDto> {
    const response =
      await axiosInstance.post<BatteryConcertApplicationStatusDto>(
        `/battery-proposal/v2/${proposalId}/concert-application/in-person`,
        body,
      )

    return response.data
  }

  async uploadConcertDocument(
    proposalId: string,
    stipulationId: string,
    body: UploadBatteryConcertDocumentDto,
  ): Promise<BatteryConcertApplicationStatusDto> {
    const response =
      await axiosInstance.post<BatteryConcertApplicationStatusDto>(
        `/battery-proposal/v2/${proposalId}/concert-application/stipulations/${stipulationId}/uploads`,
        body,
      )

    return response.data
  }

  /** Ask Concert to build the signable loan documents for an approved application. */
  async generateConcertLoanDocuments(
    proposalId: string,
  ): Promise<BatteryConcertApplicationStatusDto> {
    const response =
      await axiosInstance.post<BatteryConcertApplicationStatusDto>(
        `/battery-proposal/v2/${proposalId}/concert-application/loan-documents`,
      )

    return response.data
  }

  /** Send the generated Battery loan documents to the homeowner for signature. */
  async sendConcertLoanDocuments(
    proposalId: string,
  ): Promise<BatteryConcertApplicationStatusDto> {
    const response =
      await axiosInstance.post<BatteryConcertApplicationStatusDto>(
        `/battery-proposal/v2/${proposalId}/concert-application/loan-documents/send`,
      )

    return response.data
  }

  /** Read the supplemental package Vision sends alongside Concert's documents. */
  async getConcertSupplemental(
    proposalId: string,
  ): Promise<BatterySupplementalStatusDto> {
    const response = await axiosInstance.get<BatterySupplementalStatusDto>(
      `/battery-proposal/v2/${proposalId}/concert-application/supplemental-documents`,
    )

    return response.data
  }

  /** First send for a confirmed Concert package that never had an attempt. */
  async sendConcertSupplemental(
    proposalId: string,
  ): Promise<BatterySupplementalStatusDto> {
    const response = await axiosInstance.post<BatterySupplementalStatusDto>(
      `/battery-proposal/v2/${proposalId}/concert-application/supplemental-documents/send`,
    )

    return response.data
  }

  async retryConcertSupplemental(
    proposalId: string,
    attemptId: string,
  ): Promise<BatterySupplementalStatusDto> {
    const response = await axiosInstance.post<BatterySupplementalStatusDto>(
      `/battery-proposal/v2/${proposalId}/concert-application/supplemental-documents/retry`,
      { attemptId },
    )

    return response.data
  }

  /** Re-email the existing envelope; never regenerates the package. */
  async resendConcertSupplemental(
    proposalId: string,
  ): Promise<BatterySupplementalStatusDto> {
    const response = await axiosInstance.post<BatterySupplementalStatusDto>(
      `/battery-proposal/v2/${proposalId}/concert-application/supplemental-documents/resend`,
    )

    return response.data
  }

  async submitConcertDocumentsForReview(
    proposalId: string,
  ): Promise<BatteryConcertApplicationStatusDto> {
    const response =
      await axiosInstance.post<BatteryConcertApplicationStatusDto>(
        `/battery-proposal/v2/${proposalId}/concert-application/supporting-documents/submit-for-review`,
      )

    return response.data
  }

  async getIncentives(
    proposalId: string,
  ): Promise<BatteryProposalIncentivesDto> {
    const response = await axiosInstance.get<BatteryProposalIncentivesDto>(
      `/battery-proposal/v2/${proposalId}/incentives`,
    )

    return response.data
  }

  async updateIncentives(
    proposalId: string,
    body: UpdateBatteryProposalIncentivesDto,
  ): Promise<BatteryProposalIncentivesDto> {
    const response = await axiosInstance.put<BatteryProposalIncentivesDto>(
      `/battery-proposal/v2/${proposalId}/incentives`,
      body,
    )

    return response.data
  }

  async getVppPrograms(
    proposalId: string,
  ): Promise<BatteryProposalVppProgramsDto> {
    const response = await axiosInstance.get<BatteryProposalVppProgramsDto>(
      `/battery-proposal/v2/${proposalId}/vpp-programs`,
    )

    return response.data
  }

  async updateVppPrograms(
    proposalId: string,
    body: UpdateBatteryProposalVppProgramsDto,
  ): Promise<BatteryProposalVppProgramsDto> {
    const response = await axiosInstance.put<BatteryProposalVppProgramsDto>(
      `/battery-proposal/v2/${proposalId}/vpp-programs`,
      body,
    )

    return response.data
  }

  /** The saved AI compute node, its estimated income and the financing lock. No provider call. */
  async getCompute(proposalId: string): Promise<BatteryProposalComputeDto> {
    const response = await axiosInstance.get<BatteryProposalComputeDto>(
      `/battery-proposal/v2/${encodeURIComponent(proposalId)}/compute`,
    )

    return response.data
  }

  /** The AI compute nodes PowurOS offers on the saved equipment and Finance Plan; writes nothing. */
  async previewCompute(
    proposalId: string,
    signal?: AbortSignal,
  ): Promise<BatteryComputePreviewDto> {
    const response = await axiosInstance.post<BatteryComputePreviewDto>(
      `/battery-proposal/v2/${encodeURIComponent(proposalId)}/compute/preview`,
      undefined,
      { signal },
    )

    return response.data
  }

  /** Saves the AI compute node, or none. The answer is the compute read. */
  async putCompute(
    proposalId: string,
    body: UpdateBatteryProposalComputeDto,
  ): Promise<BatteryProposalComputeDto> {
    // The save asks PowurOS what it offers, so it gets the provider timeout.
    const response = await axiosInstance.put<BatteryProposalComputeDto>(
      `/battery-proposal/v2/${encodeURIComponent(proposalId)}/compute`,
      body,
      { timeout: PROVIDER_WRITE_TIMEOUT_MS },
    )

    return response.data
  }

  async getExtendedWarranties(
    proposalId: string,
  ): Promise<BatteryProposalExtendedWarrantiesDto> {
    const response =
      await axiosInstance.get<BatteryProposalExtendedWarrantiesDto>(
        `/battery-proposal/v2/${proposalId}/extended-warranties`,
      )

    return response.data
  }

  async updateExtendedWarranty(
    proposalId: string,
    body: UpdateBatteryProposalExtendedWarrantyDto,
  ): Promise<BatteryProposalDto> {
    const response = await axiosInstance.put<BatteryProposalDto>(
      `/battery-proposal/v2/${proposalId}/extended-warranty`,
      body,
    )

    return response.data
  }

  async getInputs(proposalId: string): Promise<BatteryProposalInputsDto> {
    const response = await axiosInstance.get<BatteryProposalInputsDto>(
      `/battery-proposal/v2/${proposalId}/inputs`,
    )

    return response.data
  }

  async fetchDebugInputs(
    proposalId: string,
    signal?: AbortSignal,
  ): Promise<ProposalDebugRequestBodyDto> {
    return fetchDebugRequest(proposalId, `inputs`, signal)
  }

  async fetchDebugPricing(
    proposalId: string,
    signal?: AbortSignal,
  ): Promise<ProposalDebugRequestBodyDto> {
    return fetchDebugRequest(proposalId, `pricing`, signal)
  }

  async previewEquipment(
    proposalId: string,
    body: PreviewBatteryProposalEquipmentDto,
    signal?: AbortSignal,
  ): Promise<BatteryProposalInputsDto> {
    const response = await axiosInstance.post<BatteryProposalInputsDto>(
      `/battery-proposal/v2/${proposalId}/equipment-preview`,
      body,
      { signal },
    )

    return response.data
  }

  async updateBatteryProposalDetails(
    proposalId: string,
    body: UpdateBatteryProposalDetailsDto,
  ): Promise<BatteryProposalDto> {
    const response = await axiosInstance.patch<BatteryProposalDto>(
      `/battery-proposal/v2/${proposalId}/details`,
      body,
    )

    return response.data
  }

  async ensureGenabilityAccount(
    proposalId: string,
  ): Promise<BatteryProposalGenabilityAccountDto> {
    const response =
      await axiosInstance.put<BatteryProposalGenabilityAccountDto>(
        `/battery-proposal/v2/${proposalId}/genability-account`,
      )

    return response.data
  }

  async getUtilityCompanies(
    proposalId: string,
  ): Promise<BatteryProposalUtilityCompanyDto[]> {
    return (
      await axiosInstance.get<BatteryProposalUtilityCompanyDto[]>(
        `/battery-proposal/v2/${proposalId}/utility-companies`,
      )
    ).data
  }

  async getUtilityTariffs(
    proposalId: string,
    lseId: number,
  ): Promise<BatteryProposalTariffCandidateDto[]> {
    return (
      await axiosInstance.get<BatteryProposalTariffCandidateDto[]>(
        `/battery-proposal/v2/${proposalId}/utility-companies/${lseId}/tariffs`,
      )
    ).data
  }

  async updateTariff(
    proposalId: string,
    body: UpdateBatteryProposalTariffDto,
  ): Promise<BatteryProposalDto> {
    const response = await axiosInstance.patch<BatteryProposalDto>(
      `/battery-proposal/v2/${proposalId}/tariff`,
      body,
    )

    return response.data
  }

  async updateExistingBattery(
    proposalId: string,
    body: UpdateBatteryExistingBatteryDto,
  ): Promise<BatteryProposalDto> {
    const response = await axiosInstance.put<BatteryProposalDto>(
      `/battery-proposal/v2/${proposalId}/existing-battery`,
      body,
    )

    return response.data
  }

  async getArbitrage(proposalId: string): Promise<BatteryArbitrageDto> {
    const response = await axiosInstance.get<BatteryArbitrageDto>(
      `/battery-proposal/v2/${proposalId}/arbitrage`,
    )

    return response.data
  }

  /**
   * Partial save: omitted fields keep their stored value. A changed value
   * clears the published result server-side, so the response is the new
   * lifecycle state and never the previous savings.
   */
  async updateArbitrageSettings(
    proposalId: string,
    body: UpdateBatteryArbitrageSettingsDto,
  ): Promise<BatteryArbitrageDto> {
    const response = await axiosInstance.patch<BatteryArbitrageDto>(
      `/battery-proposal/v2/${proposalId}/arbitrage/settings`,
      body,
    )

    return response.data
  }

  async setArbitrageVisibility(
    proposalId: string,
    visibility: BatterySavingsVisibility,
  ): Promise<BatteryArbitrageDto> {
    const response = await axiosInstance.patch<BatteryArbitrageDto>(
      `/battery-proposal/v2/${proposalId}/arbitrage/visibility`,
      { visibility },
    )

    return response.data
  }

  async updateArbitrageSavingsInput(
    proposalId: string,
    body: UpdateBatterySavingsInputDto,
  ): Promise<BatteryArbitrageDto> {
    const response = await axiosInstance.patch<BatteryArbitrageDto>(
      `/battery-proposal/v2/${proposalId}/arbitrage/savings-input`,
      body,
    )

    return response.data
  }

  /** Bodyless: queues a fresh attempt from committed inputs. Answers 202. */
  async retryArbitrage(proposalId: string): Promise<BatteryArbitrageDto> {
    const response = await axiosInstance.post<BatteryArbitrageDto>(
      `/battery-proposal/v2/${proposalId}/arbitrage/retry`,
    )

    return response.data
  }

  async updateAdders(
    proposalId: string,
    body: UpdateBatteryProposalAddersDto,
  ): Promise<BatteryProposalDto> {
    const response = await axiosInstance.patch<BatteryProposalDto>(
      `/battery-proposal/v2/${proposalId}/adders`,
      body,
    )

    return response.data
  }

  async refreshAdders(
    proposalId: string,
  ): Promise<RefreshBatteryProposalAddersResultDto> {
    const response =
      await axiosInstance.post<RefreshBatteryProposalAddersResultDto>(
        `/battery-proposal/v2/${proposalId}/adders/refresh`,
      )

    return response.data
  }
}

export const batteryProposalService = new BatteryProposalService()
